import { isAdmin, isCompanyUser, isTerminal, isSkipper, isOperator, isCustomer, isMainUserLimited } from "imports/Utils";
import { showSnackbar } from "@cofano/cck";
import { Coastguard } from "@cofano/coastguard2-frontend-client";
import { CallType, DocumentEntity, DocumentStatus, DocumentType, StatusType } from "__generated__/graphql";
import { Entity, QueriedDocument } from "components/documents/Documents";
import { isShipManager } from "components/utils/Permission";
import { ExceptionErrorContent } from "components/utils/Request2";
import { CargoLink, DossierLink, VoyageLink } from "utils/LinkUtils";
import CargoActionTypeTag from "view/cargos/components/tags/CargoActionTypeTag";
import { intl } from "LocaleWrapper";
import { Session } from "ezStore";

export const getCanSeeCargoDocuments = (session: Session) => {
	return isAdmin(session) || isCompanyUser(session) || isMainUserLimited(session) || isTerminal(session) || isSkipper(session) || isShipManager(session) || isOperator(session) || isCustomer(session);
};

export const getDocumentStatusColor = (documentStatus: DocumentStatus) => {
	switch (documentStatus) {
		case DocumentStatus.Created:
			return "#f68010";
		case DocumentStatus.Processed:
			return "#74e12a";
		case DocumentStatus.Published:
			return "#bf8dc4";
		default:
			return "#f4f4f4";
	}
};

export const cargoActionTypeToDocumentType = (cargoActionType: CallType) => {
	switch (cargoActionType) {
		case CallType.Loading:
			return DocumentEntity.LoadingCargo;
		case CallType.Discharging:
			return DocumentEntity.DischargingCargo;
		case CallType.NitrogenPurging:
			return DocumentEntity.NitrogenPurging;
		case CallType.Cleaning:
			return DocumentEntity.TankCleaning;
		default:
			return DocumentEntity.LoadingCargo;
	}
};

export const statusTypeToDocumentEntity = (statusType: StatusType): DocumentEntity | undefined => {
	switch (statusType) {
		case StatusType.Cargo:
			return DocumentEntity.Cargo;
		case StatusType.Call:
			return DocumentEntity.Call;
		case StatusType.Voyage:
			return DocumentEntity.Voyage;
		case StatusType.Invoice:
			return DocumentEntity.Invoice;
		default:
			// declarations have no matching DocumentEntity
			return undefined;
	}
};

export type UploadDocumentData = {
	description?: string;
	documentType?: DocumentType;
	issuedAt?: number;
	validUntil?: number;
};

export const buildDocumentFormData = (file: File, entity: Entity, data: UploadDocumentData) => {
	const formData = new FormData();

	formData.append("document", file);
	formData.append("description", data.description ?? "");
	formData.append("documentType", data.documentType ?? DocumentType.Other);
	formData.append("documentEntity", "" + entity.type);

	if (data.issuedAt) {
		formData.append("issuedAt", new Date(data.issuedAt).toISOString());
	}

	if (data.validUntil) {
		formData.append("validUntil", "" + data.validUntil);
	}

	switch (entity.type) {
		case DocumentEntity.Voyage:
			formData.set("voyageId", "" + entity.id);
			break;

		case DocumentEntity.Call:
			formData.set("callId", "" + entity.id);
			break;

		case DocumentEntity.Cargo:
			formData.set("cargoId", "" + entity.id);
			break;

		case DocumentEntity.LoadingCargo:
		case DocumentEntity.DischargingCargo:
		case DocumentEntity.NitrogenPurging:
		case DocumentEntity.TankCleaning:
			formData.set("cargoId", "" + entity.id);

			if (entity.callId) {
				formData.append("callId", "" + entity.callId);
			}
			if (entity.cargoActionId) {
				formData.append("cargoActionId", "" + entity.cargoActionId);
			}
			break;

		case DocumentEntity.Contract:
			formData.set("contractId", "" + entity.id);
			break;

		case DocumentEntity.Invoice:
			formData.set("invoiceId", "" + entity.id);
			break;

		case DocumentEntity.Ship:
			formData.set("shipId", "" + entity.id);
			break;

		case DocumentEntity.Product:
			formData.set("productId", "" + entity.id);
			break;

		case DocumentEntity.Location:
			formData.set("locationId", "" + entity.id);
			break;

		case DocumentEntity.Manual:
			formData.set("manualPermission", "" + entity.id);
			break;

		case DocumentEntity.Charter:
			formData.set("charterId", "" + entity.id);
			break;
	}

	return formData;
};

export const uploadDocuments = async (
	instance: Coastguard,
	files: File[],
	entities: Entity[],
	data: UploadDocumentData = {},
) => {
	const responses = await Promise.all(entities.flatMap(entity => files.map(file =>
		instance.fetch("/documents", {
			method: "POST",
			body: buildDocumentFormData(file, entity, data),
		}))));

	const results = await Promise.all(responses.map(response => response.json()));

	const failed = results.filter(item => item.error);

	failed.forEach(exception => {
		showSnackbar(<ExceptionErrorContent response={exception} />, { variant: "error" });
	});

	return {
		failed,
		succeeded: results.filter(item => !item.error),
	};
};

export const buildDocumentDescription = (files: File[]) => files
	.map(file => file.name.split(".")[0].replaceAll("_", " ").replaceAll("-", " "))
	.join(", ");

export const buildDocumentUrl = (
	url: string,
	queryParams: Record<string, string | boolean | number | undefined> | undefined,
	language: string | undefined,
	preview: boolean,
	useAbsolutePath: boolean,
) => {
	let baseUrl = url.split("?")[0];
	if (baseUrl.startsWith("/")) {
		baseUrl = baseUrl.substring(1);
	}

	const params = new URLSearchParams(url.split("?")[1]);

	if (queryParams) {
		for (const [ key, value ] of Object.entries(queryParams)) {
			if (value === undefined) {
				continue;
			}
			params.append(key, value.toString());
		}
	}

	if (language) {
		params.append("languageCode", language);
	}

	if (preview) {
		params.append("preview", "true");
	}

	return `${useAbsolutePath ? "/" : ""}${baseUrl}?${params.toString()}`;
};


export const DocumentLink = ({ document }: { document: QueriedDocument }) => {
	switch (document.documentEntity) {
		case DocumentEntity.Cargo:
			return <>{document.cargo && <CargoLink cargo={document.cargo} />}</>;
		case DocumentEntity.NitrogenPurging:
			return <>{document.cargo && <CargoLink cargo={document.cargo} />} <CargoActionTypeTag type={CallType.NitrogenPurging} /></>;
		case DocumentEntity.LoadingCargo:
			return <>{document.cargo && <CargoLink cargo={document.cargo} />} <CargoActionTypeTag type={CallType.Loading} /></>;
		case DocumentEntity.DischargingCargo:
			return <>{document.cargo && <CargoLink cargo={document.cargo} />} <CargoActionTypeTag type={CallType.Discharging} /></>;
		case DocumentEntity.TankCleaning:
			return <>{document.cargo && <CargoLink cargo={document.cargo} />} <CargoActionTypeTag type={CallType.Cleaning} /></>;
		case DocumentEntity.Charter:
			return <><DossierLink charter={document.charter} /></>;
		case DocumentEntity.Voyage:
			return <>{document.voyage && <VoyageLink voyage={document.voyage} />}</>;
		default:
			return <>{intl.formatMessage({ id: document.documentEntity.toUpperCase() ?? " " })}</>;
	}
};

export const getDocumentsCardTitle = (entities: Entity[]) => {
	const types = [ ...new Set(entities.flatMap(x => x.type)) ];
	if (types.length === 1) {
		switch (types[0]) {
			case DocumentEntity.LoadingCargo:
				return `${intl.formatMessage({ id: "LOADING" })} ${intl.formatMessage({ id: "DOCUMENTS" })}`;

			case DocumentEntity.DischargingCargo:
				return `${intl.formatMessage({ id: "DISCHARGING" })} ${intl.formatMessage({ id: "DOCUMENTS" })}`;

			case DocumentEntity.NitrogenPurging:
				return `${intl.formatMessage({ id: "NITROGEN_PURGING" })} ${intl.formatMessage({ id: "DOCUMENTS" })}`;

			case DocumentEntity.TankCleaning:
				return `${intl.formatMessage({ id: "TANK_CLEANING" })} ${intl.formatMessage({ id: "DOCUMENTS" })}`;

			case DocumentEntity.Voyage:
				return `${intl.formatMessage({ id: "VOYAGE" })} ${intl.formatMessage({ id: "DOCUMENTS" })}`;

			case DocumentEntity.Charter:
				return `${intl.formatMessage({ id: "CHARTER" })} ${intl.formatMessage({ id: "DOCUMENTS" })}`;
		}
	}

	return intl.formatMessage({ id: "ORDER_DOCUMENTS" });
};

// Reference: https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Content-Disposition
export const extractFilenameFromContentDispositionHeader = (headers: Headers): string | null => {
	const contentDisposition = headers.get("Content-Disposition");

	if (!contentDisposition) {
		return null;
	}

	// 1. Prioritize filename*= (RFC 5987) as it handles special characters best.
	// This regex safely ignores other attributes and just grabs the URI-encoded part.
	const filenameStarRegex = /filename\*=[A-Za-z0-9_-]+''([^;]*)/i;
	const filenameStarMatch = contentDisposition.match(filenameStarRegex);

	if (filenameStarMatch && filenameStarMatch[1]) {
		try {
			return decodeURIComponent(filenameStarMatch[1]);
		} catch (e) {
			console.warn("Failed to decode filename* value, falling back...", e);
		}
	}

	// 2. Fall back to standard filename=
	// This regex correctly handles both quoted and unquoted filenames
	const filenameRegex = /filename=(["']?)([^"';]+)\1/i;
	const filenameMatch = contentDisposition.match(filenameRegex);

	if (filenameMatch && filenameMatch[2]) {
		return filenameMatch[2].trim();
	}

	return null;
};
